import { Button } from "@components/ui/button"
import { Loader2, MessageCircleWarning } from "lucide-react"
import { useAccountOfferFunnel } from "../utils/useAccountOfferFunnel"
import {
  useOffersDetailSerializer,
  type ISerializedOfferDetailData,
} from "../utils/useOffersDetailSerializer"
import { DataTable } from "@components/DataTable/DataTable"
import { TpvTypesSelect } from "../TpvTypeSelect"
import { retentionRateTableColumns } from "../utils/tableColumnSchema"
import { useCopyShortkeyLink } from "../utils/useCopyShortkeyLink"
import { useHasPermissions } from "@hooks/useHasPermissions"
import type { IWinbackOffer } from "@apis/winbacks/types"
import type { IRetentionOffer } from "@apis/retention/types"

export const OfferDetail = () => {
  const { offerType, setStep, selectedOffer, account_id, tpvType, setTpvType } =
    useAccountOfferFunnel()
  const { data, isError, isPending, error } = useOffersDetailSerializer(
    account_id,
    selectedOffer as IWinbackOffer | IRetentionOffer,
  )

  const { canCancel } = useHasPermissions()
  const { copyShortkeyLink, isCopying } = useCopyShortkeyLink()

  if (isPending) {
    return (
      <div className="tw:flex tw:justify-center">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="tw:py-4 tw:text-red-500">
        Error loading offer details. {error}
      </div>
    )
  }

  const renderOfferDetails = (offerDetails: ISerializedOfferDetailData) => {
    return offerDetails.map((item, index) => {
      return (
        <div key={`${item.title}-${index}`}>
          <p className="tw:font-bold">{item.title}</p>
          {Array.isArray(item.value) ? (
            item.value.length ? (
              <>
                {item.value.map((val, i) => (
                  <p key={`${val}-${i}`}>{val}</p>
                ))}
                {item.alert && (
                  <div className="tw:text-destructive">
                    <MessageCircleWarning
                      size={16}
                      className="tw:inline-block"
                    />
                    <span className="tw:pl-1">{item.alert}</span>
                  </div>
                )}
              </>
            ) : (
              <p>N/A</p>
            )
          ) : (
            <p className="tw:break-words">{item.value || "N/A"}</p>
          )}
        </div>
      )
    })
  }

  return (
    <div className="tw:flex tw:flex-col tw:items-start tw:gap-4">
      <h4>{data.long_script}</h4>

      {offerType === "Retention" && (
        <DataTable
          columns={retentionRateTableColumns}
          data={selectedOffer?.retention_details?.cycles ?? []}
        />
      )}

      <div className="tw:bordered-container tw:grid tw:grid-cols-2 tw:gap-4 tw:bg-gray-50">
        <div>{renderOfferDetails(data.oldOffer)}</div>
        <div>{renderOfferDetails(data.newOffer)}</div>
        <Button
          variant="warning"
          className="tw:col-span-2 tw:self-end"
          onClick={() =>
            copyShortkeyLink(
              account_id,
              selectedOffer?.offer_code as string,
              "WINBACK",
            )
          }
        >
          {isCopying ? (
            <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
          ) : (
            "Copy to clipboard"
          )}
        </Button>
      </div>

      <TpvTypesSelect tpvType={tpvType} setTpvType={setTpvType} />

      <div className="tw:flex tw:w-full tw:justify-between">
        <Button variant="secondary" onClick={() => setStep("chooseOffer")}>
          Back to Offers
        </Button>
        <div className="tw:flex tw:gap-2">
          <Button variant="default" onClick={() => setStep("verification")}>
            Accept Offer
          </Button>
          {offerType === "Retention" && (
            <Button
              variant="destructive"
              onClick={() => setStep("cancelMember")}
              hidden={!canCancel}
            >
              Cancel Account
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
